import * as React from "react";
import { Info, Shield, Users } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { TextInput } from "../../../ui/components/Form";
import { ColorPicker } from "../../../ui/components/ColorPicker";
import {
  createOrganizationRole,
  updateOrganizationRole,
  type OrgAssignablePermission,
  type OrganizationMember,
  type OrganizationScopedRole,
} from "../../lib/api/users";
import { RoleMembersModal } from "./RoleMembersModal";
import { useI18n } from "../../lib/i18n";
import * as confirmStyles from "./OrganizationDetailPage.css";
import * as styles from "./RoleFormModal.css";

const DEFAULT_COLOR = "#6B7280";

export interface RoleFormModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  orgId: string;
  accessToken: string;
  availablePermissions: OrgAssignablePermission[];
  /** When provided, editing an existing role. Otherwise creating. */
  existing?: OrganizationScopedRole;
  members?: OrganizationMember[];
  memberRoleId?: string | null;
  activeAdminCount?: number;
  onSaved: (role: OrganizationScopedRole) => void;
  onMemberUpdated?: (userId: string) => void;
}

function permissionLabel(name: string): string {
  return name
    .replace(/:/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function RoleFormModal({
  open,
  setOpen,
  orgId,
  accessToken,
  availablePermissions,
  existing,
  members = [],
  memberRoleId = null,
  activeAdminCount = 0,
  onSaved,
  onMemberUpdated,
}: RoleFormModalProps) {
  const { t } = useI18n();
  const isAdminLocked = existing?.systemKey === "admin";
  const isMember = existing?.systemKey === "member";
  const readOnly = isAdminLocked;

  const [label, setLabel] = React.useState("");
  const [color, setColor] = React.useState(DEFAULT_COLOR);
  const [selectedPerms, setSelectedPerms] = React.useState<Set<string>>(
    new Set(),
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [membersModalOpen, setMembersModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setMembersModalOpen(false);
      return;
    }
    setError(null);
    if (existing) {
      setLabel(existing.label?.trim() || existing.name);
      setColor(existing.color || DEFAULT_COLOR);
      setSelectedPerms(new Set(existing.permissions ?? []));
    } else {
      setLabel("");
      setColor(DEFAULT_COLOR);
      setSelectedPerms(new Set(["org:read"]));
    }
  }, [open, existing]);

  const togglePerm = (name: string) => {
    if (readOnly) return;
    if (isMember && name === "org:read") return;
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSave = async () => {
    if (readOnly) {
      setOpen(false);
      return;
    }
    const trimmed = label.trim();
    if (!trimmed) {
      setError("Label is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const permissionNames = [...selectedPerms];
      if (isMember && !permissionNames.includes("org:read")) {
        permissionNames.push("org:read");
      }
      const saved = existing
        ? await updateOrganizationRole(accessToken, orgId, existing.id, {
            label: isMember ? undefined : trimmed,
            color,
            permissionNames,
          })
        : await createOrganizationRole(accessToken, orgId, {
            label: trimmed,
            color,
            permissionNames,
          });
      onSaved({
        ...saved,
        label: saved.label ?? trimmed,
        color: saved.color ?? color,
        permissions: saved.permissions ?? permissionNames,
      });
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  const initialSnapshot = React.useMemo(() => {
    if (!existing) {
      return {
        label: "",
        color: DEFAULT_COLOR,
        perms: ["org:read"] as string[],
      };
    }
    return {
      label: existing.label?.trim() || existing.name,
      color: (existing.color || DEFAULT_COLOR).toUpperCase(),
      perms: [...(existing.permissions ?? [])].sort(),
    };
  }, [existing]);

  const isDirty = React.useMemo(() => {
    if (readOnly) return false;
    if (label.trim() !== initialSnapshot.label) return true;
    if (color.toUpperCase() !== initialSnapshot.color) return true;
    const current = [...selectedPerms].sort();
    if (current.length !== initialSnapshot.perms.length) return true;
    return current.some((p, i) => p !== initialSnapshot.perms[i]);
  }, [readOnly, label, color, selectedPerms, initialSnapshot]);

  const grouped = React.useMemo(() => {
    const map = new Map<string, OrgAssignablePermission[]>();
    for (const p of availablePermissions) {
      const resource = p.resource || p.name.split(":")[0] || "other";
      const list = map.get(resource) ?? [];
      list.push(p);
      map.set(resource, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [availablePermissions]);

  const title = existing
    ? isAdminLocked
      ? "Admin role"
      : "Edit Role"
    : "Add role";

  const saveDisabled =
    saving ||
    (existing ? !isDirty : !label.trim());

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        minWidth="520px"
        maxWidth="640px"
        minHeight="auto"
      >
        <div className={confirmStyles.confirmModalBody}>
          <h3 className={confirmStyles.confirmModalTitle}>{title}</h3>
          <div className={styles.form}>
            {error && <Callout variant="error">{error}</Callout>}
            {isAdminLocked && (
              <Callout variant="info" icon={<Shield size={14} aria-hidden />}>
                The Admin role always has all organization permissions and cannot
                be edited.
              </Callout>
            )}
            {isMember && !isAdminLocked && (
              <Callout variant="info" icon={<Info size={14} aria-hidden />}>
                Member is a required system role. Permissions can be adjusted, but
                org:read cannot be removed.
              </Callout>
            )}

            <TextInput
              label={t("pro.orgs.roleLabel")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={readOnly || isMember || saving}
              placeholder="e.g. Fleet Lead"
            />

            <ColorPicker
              label={t("pro.orgs.roleColor")}
              value={color}
              onChange={setColor}
              disabled={readOnly || saving}
            />

            <div className={styles.field}>
              <Text level="body-small" weight="medium" className={styles.fieldLabel}>
                Permissions
              </Text>
              <div className={styles.permGroups}>
                {grouped.length === 0 ? (
                  <Text level="body-small" style={{ color: "var(--color-fgMuted, #9CA3AF)" }}>
                    No permissions available.
                  </Text>
                ) : (
                  grouped.map(([resource, perms]) => (
                    <div key={resource} className={styles.permGroup}>
                      <span className={styles.permGroupTitle}>{resource}</span>
                      <div className={styles.permList}>
                        {perms.map((p) => {
                          const lockedFloor = isMember && p.name === "org:read";
                          return (
                            <label key={p.id || p.name} className={styles.permItem}>
                              <input
                                type="checkbox"
                                className={styles.permCheckbox}
                                checked={selectedPerms.has(p.name) || isAdminLocked}
                                disabled={readOnly || lockedFloor || saving}
                                onChange={() => togglePerm(p.name)}
                              />
                              <span>
                                <span className={styles.permName}>
                                  {permissionLabel(p.name)}
                                </span>
                                {p.description ? (
                                  <span className={styles.permDesc}>
                                    {p.description}
                                  </span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className={styles.footer}>
              <div className={styles.footerLeft}>
                {existing ? (
                  <Button
                    type="button"
                    color="neutral"
                    variant="outline"
                    size="md"
                    iconLeft={<Users size={16} />}
                    disabled={saving}
                    onClick={() => setMembersModalOpen(true)}
                  >
                    Members with this role
                  </Button>
                ) : null}
              </div>
              <div className={styles.footerRight}>
                <Button
                  color="neutral"
                  variant="outline"
                  size="md"
                  disabled={saving}
                  onClick={() => setOpen(false)}
                >
                  {readOnly ? "Close" : "Cancel"}
                </Button>
                {!readOnly && (
                  <Button
                    color="primary"
                    size="md"
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                  >
                    {saving ? "Saving…" : existing ? "Save" : "Create role"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>
      {existing && (
        <RoleMembersModal
          open={membersModalOpen}
          setOpen={setMembersModalOpen}
          orgId={orgId}
          accessToken={accessToken}
          role={existing}
          members={members}
          memberRoleId={memberRoleId}
          activeAdminCount={activeAdminCount}
          onMemberUpdated={(userId) => {
            onMemberUpdated?.(userId);
          }}
        />
      )}
    </>
  );
}
